import "./CalendarPassQuestionScreen.css";
import nextIcon from "../../../assets/images/score-card-next-icon.svg";
import appIcon from "../../../assets/images/header-sabehub-app-icon.svg";
import backIcon from "../../../assets/images/calendar-pass-question-back-icon.svg";
import emptyStar from "../../../assets/images/score-card/score-card-empty-star.svg";
import filledStar from "../../../assets/images/score-card/score-card-filled-star.svg";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { highlightLastWord } from "utils/utils";
import { useMutation } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import ROUTE_PATHS from "utils/routePaths";

interface PassAnswer {
  id: string;
  title: string;
  description: string | null;
  banner_image: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

interface SlotPassQuestion {
  id: string;
  stage: number;
  step: number;
  slot_pass_type: string;
  question: string;
  answers: PassAnswer[] | null;
  description?: string | null;
  placeholder?: string | null;
  is_multi_select: boolean;
  is_optional: boolean;
}

const CalendarPassQuestionScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateReservation } = useWSMApi();
  const questions: SlotPassQuestion[] = location.state?.questions ?? [];
  const stage: string = location.state?.stage ?? "";
  const slotId: string = location.state?.slot_id ?? "";
  const slotPassType: string | undefined = location.state?.slot_pass_type;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  // comment text per question id
  const [comments, setComments] = useState<Record<string, string>>({});
  const currentQuestion = questions[currentQuestionIndex] ?? null;
  const placeholder = currentQuestion?.placeholder ?? "";
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const hasOptions =
    currentQuestion?.answers != null && currentQuestion.answers.length > 0;
  const showTextarea = !hasOptions;
  const selectedOptions: string[] = currentQuestion
    ? (answers[currentQuestion.id] ?? [])
    : [];
  const commentText: string = currentQuestion
    ? (comments[currentQuestion.id] ?? "")
    : "";

  // Disabled when:
  // - question is NOT optional AND has options but none are selected, OR
  // - question is NOT optional AND has no options (textarea) but comment is empty
  const isNextDisabled = (() => {
    if (!currentQuestion) return true;
    if (currentQuestion.is_optional) return false;
    if (hasOptions) return selectedOptions.length === 0;
    return commentText.trim().length === 0;
  })();

  const updateReservationMutation = useMutation({
    mutationFn: (data: any) => updateReservation(data),
    onSuccess: () => {
      if (stage === "4") {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_LAST_STAGE_THANK_YOU_SCREEN);
      } else {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PASS_THANK_YOU_SCREEN);
      }
    },
  });

  const handleOptionSelect = (answerId: string) => {
    if (!currentQuestion) return;

    if (currentQuestion.is_multi_select) {
      setAnswers((prev) => {
        const existing = prev[currentQuestion.id] ?? [];
        const updated = existing.includes(answerId)
          ? existing.filter((o) => o !== answerId)
          : [...existing, answerId];
        return { ...prev, [currentQuestion.id]: updated };
      });
    } else {
      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.id]: [answerId],
      }));
    }
  };

  const handleCommentChange = (text: string) => {
    if (!currentQuestion) return;
    setComments((prev) => ({ ...prev, [currentQuestion.id]: text }));
  };

  const handleNext = () => {
    if (isNextDisabled || !currentQuestion) return;

    // Capture the final answers state including the current question
    const finalAnswers = { ...answers };
    const finalComments = { ...comments };

    if (!isLastQuestion) {
      setCurrentQuestionIndex((prev) => prev + 1);
      return;
    }

    // Build payload: one object per question
    const selectedOptionsPayload = questions.map((q) => {
      const selectedIds = finalAnswers[q.id] ?? [];
      // Resolve selected answer titles from the question's answers array
      const answerTitles = (q.answers ?? [])
        .filter((a) => selectedIds.includes(a.id))
        .map((a) => a.title);
      const comment = finalComments[q.id]?.trim() ?? "";

      return {
        question: q.question,
        answers: answerTitles,
        ...(comment ? { comment } : {}),
      };
    });

    const stageKey =
      stage === "4"
        ? "fourth_stage_selected_options"
        : "first_stage_selected_options";

    updateReservationMutation.mutate({
      slot_id: slotId,
      ...(slotPassType ? { slot_pass_type: slotPassType } : {}),
      [stageKey]: selectedOptionsPayload,
    });
  };

  const handleBack = () => {
    if (currentQuestionIndex === 0) {
      navigate(-1);
      return;
    }
    setCurrentQuestionIndex((prev) => prev - 1);
  };

  if (!currentQuestion) return null;

  return (
    <div className="wms-mobile-container">
      <div className="calendar-pass-question-screen-container">
        <div className="calendar-pass-question-screen-header-sticky">
          <header className="calendar-pass-question-screen-header-bar">
            <img
              src={appIcon}
              alt="app-logo"
              className="calendar-pass-question-screen-header-app-icon"
            />
            <div className="calendar-pass-question-screen-header-divider"></div>
            <span>
              {highlightLastWord(
                stage === "4" ? "Reflection Time" : "Intention Setting",
              )}
            </span>
          </header>
        </div>

        <div className="calendar-pass-question-screen-content-container">
          {currentQuestion.description && (
            <div className="screen-title-container">
              {currentQuestion.description}
            </div>
          )}

          <div className="question-container">
            <span>{currentQuestionIndex + 1}.</span>
            <span>{highlightLastWord(currentQuestion.question, 4)}</span>
          </div>

          {/* Options — only rendered when the question has answers */}
          {hasOptions && (
            <div className="options-container">
              {currentQuestion.answers!.map((answer) => {
                const isSelected = selectedOptions.includes(answer.id);
                return (
                  <div
                    key={answer.id}
                    className={`option-card ${isSelected ? "selected" : ""}`}
                    onClick={() => handleOptionSelect(answer.id)}
                  >
                    <div className="right-container">
                      <img
                        src={isSelected ? filledStar : emptyStar}
                        alt="selection-icon"
                      />
                      {answer.description ? (
                        <div className="sub-container">
                          <span className="title">
                            {answer.title.toUpperCase()}
                          </span>
                          <span className="sub-title">
                            {answer.description}
                          </span>
                        </div>
                      ) : (
                        answer.title
                      )}
                    </div>

                    {answer.banner_image && (
                      <img
                        src={answer.banner_image}
                        alt="banner-image"
                        className="left-image-container"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Comment textarea — shown when options is null or empty */}
          {showTextarea && (
            <div className="comment-container">
              <textarea
                className="comment-textarea"
                placeholder={placeholder}
                value={commentText}
                onChange={(e) => handleCommentChange(e.target.value)}
                rows={4}
              />
            </div>
          )}

          {/* Next / Done button */}
          <div className="button-parent-container">
            <div className="back-button" onClick={handleBack}>
              <img src={backIcon} alt="back-arrow" className="button-image" />
            </div>
            <div
              className={`continue-button ${isNextDisabled ? "disabled" : ""}`}
              onClick={isNextDisabled ? undefined : handleNext}
            >
              <span>
                {isLastQuestion
                  ? currentQuestion.is_optional &&
                    selectedOptions.length === 0 &&
                    !commentText.trim()
                    ? "Skip"
                    : "Done"
                  : currentQuestion.is_optional &&
                      selectedOptions.length === 0 &&
                      !commentText.trim()
                    ? "Skip"
                    : "Next"}
              </span>
              <img src={nextIcon} alt="arrow" className="button-image" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPassQuestionScreen;
